<template>
  <div class="product-cashier-page">
    <main class="product-main">
      <section class="merchant-card">
        <div class="merchant-row">
          <div class="merchant-avatar">
            <img :src="merchantIcon" alt="" />
          </div>
          <div class="merchant-meta">
            <div class="pay-title">商品收款</div>
            <div class="merchant-name">{{ merchantDisplayName }}</div>
          </div>
        </div>

        <div class="amount-box">
          <span class="currency" :class="{ ali: isAli }">{{ currency }}</span>
          <div class="amount-text" :class="{ empty: !displayAmount }">
            {{ displayAmount || '输入金额匹配商品' }}
          </div>
        </div>

        <div class="match-hint">
          默认选中价格最接近的商品，价格相等优先
        </div>
      </section>

      <section class="product-list">
        <button
          v-for="item in matchedProducts"
          :key="item.productId"
          type="button"
          class="product-item"
          :class="{ active: item.productId === selectedProductId, exact: item.exactMatch }"
          @click="selectProduct(item)"
        >
          <div class="product-cover">
            <img :src="item.imageUrl || defaultProductImage" alt="" />
          </div>
          <div class="product-body">
            <div class="product-name">
              <span>{{ item.productName }}</span>
              <i v-if="item.customAmount" class="product-tag custom">自定义金额</i>
              <i v-else-if="item.exactMatch" class="product-tag">精准匹配</i>
            </div>
            <div class="product-meta">
              <span>{{ item.customAmount ? `按输入金额 ${displayAmount}` : formatMoney(item.amount) }}</span>
              <template v-if="!item.customAmount">
                <span v-if="item.diffAmount > 0">高于输入 {{ formatMoney(item.diffAmount) }}</span>
                <span v-else-if="item.diffAmount < 0">低于输入 {{ formatMoney(Math.abs(item.diffAmount)) }}</span>
                <span v-else>金额一致</span>
              </template>
            </div>
          </div>
        </button>
      </section>
    </main>

    <section v-if="!isFixedAmount" class="number-pad">
      <div class="key-grid">
        <button
          v-for="key in amountKeys"
          :key="key"
          type="button"
          class="num-key"
          @click="appendAmountKey(key)"
        >
          {{ key }}
        </button>
        <button type="button" class="num-key muted" @click="deleteAmountKey">⌫</button>
        <button type="button" class="num-key" @click="appendAmountKey('0')">0</button>
        <button type="button" class="num-key" @click="appendAmountKey('.')">.</button>
      </div>
      <button class="pay-button" type="button" :disabled="!canPay" @click="pay">付款</button>
    </section>

    <section v-else class="fixed-pay-bar">
      <button class="pay-button" type="button" :disabled="!canPay" @click="pay">付款</button>
    </section>
  </div>
</template>

<script>
import { getPayOrderInfo, getProductMatch, getProductPayPackage } from '@/api/api'
import config from '@/config'
import { finishSuccess } from '@/utils/closePage'
import wayCodeUtils from '@/utils/wayCode'
import defaultProductImage from '../../assets/images/default-product.jpg'

export default {
  data() {
    return {
      merchantName: '飞付通商户',
      amount: '',
      resData: {},
      merchantIcon: require('../../assets/icon/merchant.svg'),
      defaultProductImage,
      payOrderInfo: {},
      currentPayWay: wayCodeUtils.getPayWay(),
      selectedWayCode: '',
      matchedProducts: [],
      selectedProductId: '',
      selectedProduct: null,
      matchLoading: false,
      amountKeys: ['1', '2', '3', '4', '5', '6', '7', '8', '9'],
      matchTimer: null,
      matchSeq: 0
    }
  },

  computed: {
    merchantDisplayName() {
      return this.payOrderInfo.mchName || this.merchantName
    },

    currency() {
      return this.isAli ? '¥' : '￥'
    },

    isAli() {
      return this.currentPayWay && this.currentPayWay.wayCode === config.payWay.ALIPAY.wayCode
    },

    isFixedAmount() {
      return !!this.payOrderInfo.amount
    },

    displayAmount() {
      return this.amount ? String(this.amount) : ''
    },

    canPay() {
      return !!this.selectedProductId && Number(this.amount) > 0
    }
  },

  mounted() {
    document.title = '商品收款'
    this.setPayOrderInfo()
  },

  beforeDestroy() {
    if (this.matchTimer) {
      clearTimeout(this.matchTimer)
    }
  },

  methods: {
    setPayOrderInfo() {
      if (!this.currentPayWay) {
        this.$router.push({ name: config.errorPageRouteName, params: { errInfo: '请使用微信或支付宝扫码付款' } })
        return
      }

      getPayOrderInfo(this.currentPayWay.wayCode).then(res => {
        this.payOrderInfo = res
        const supportWayCodes = Array.isArray(res.supportWayCodes) ? res.supportWayCodes : []
        if (this.currentPayWay.wayCode === config.payWay.WXPAY.wayCode && supportWayCodes.indexOf(config.payWay.WXPAY.wayCode) < 0) {
          this.$router.push({ name: config.errorPageRouteName, params: { errInfo: '该商品码未开启微信扫码收款，请联系商家' } })
          return
        }
        if (this.currentPayWay.wayCode === config.payWay.ALIPAY.wayCode && supportWayCodes.indexOf(config.payWay.ALIPAY.wayCode) < 0 && supportWayCodes.indexOf(config.payWay.ALIPAY_QR.wayCode) < 0 && supportWayCodes.indexOf(config.payWay.ALIPAY_OC.wayCode) < 0) {
          this.$router.push({ name: config.errorPageRouteName, params: { errInfo: '该商品码未开启支付宝扫码收款，请联系商家' } })
          return
        }
        this.resolveSelectedWayCode(supportWayCodes)

        if (res.amount) {
          this.amount = this.formatAmount(res.amount)
          this.scheduleMatch()
        }
      }).catch(res => {
        this.$router.push({ name: config.errorPageRouteName, params: { errInfo: res.msg } })
      })
    },

    formatAmount(amountFen) {
      const amount = Number(amountFen || 0) / 100
      return amount.toFixed(2).replace(/\.?0+$/, '')
    },

    formatMoney(amountFen) {
      const amount = Number(amountFen || 0) / 100
      return amount.toFixed(2)
    },

    resolveSelectedWayCode(supportWayCodes) {
      if (this.currentPayWay.wayCode === config.payWay.WXPAY.wayCode) {
        this.selectedWayCode = config.payWay.WXPAY.wayCode
        return
      }

      if (supportWayCodes.indexOf(config.payWay.ALIPAY.wayCode) >= 0) {
        this.selectedWayCode = config.payWay.ALIPAY.wayCode
      } else if (supportWayCodes.indexOf(config.payWay.ALIPAY_QR.wayCode) >= 0) {
        this.selectedWayCode = config.payWay.ALIPAY_QR.wayCode
      } else if (supportWayCodes.indexOf(config.payWay.ALIPAY_OC.wayCode) >= 0) {
        this.selectedWayCode = config.payWay.ALIPAY_OC.wayCode
      }
    },

    tapFeedback(duration = 12) {
      if (window.navigator && typeof window.navigator.vibrate === 'function') {
        window.navigator.vibrate(duration)
      }
    },

    appendAmountKey(key) {
      this.tapFeedback()
      let value = this.amount ? String(this.amount) : ''

      if (key === '.') {
        if (value.indexOf('.') >= 0) return
        this.amount = value ? value + '.' : '0.'
        this.scheduleMatch()
        return
      }

      if (value.indexOf('.') >= 0 && value.split('.')[1].length >= 2) return
      if (value === '0') {
        this.amount = key
        this.scheduleMatch()
        return
      }
      if (value.replace('.', '').length >= 10) return

      this.amount = value + key
      this.scheduleMatch()
    },

    deleteAmountKey() {
      this.tapFeedback()
      if (!this.amount) return
      this.amount = String(this.amount).slice(0, -1)
      this.scheduleMatch()
    },

    scheduleMatch() {
      if (this.matchTimer) {
        clearTimeout(this.matchTimer)
      }
      this.matchTimer = setTimeout(() => {
        this.refreshMatch()
      }, 220)
    },

    refreshMatch() {
      if (!Number(this.amount) || Number(this.amount) <= 0) {
        this.matchedProducts = []
        this.selectedProductId = ''
        this.selectedProduct = null
        return
      }

      this.matchLoading = true
      const amountSnapshot = this.amount
      const seq = ++this.matchSeq
      getProductMatch(this.amount).then(res => {
        if (seq !== this.matchSeq || amountSnapshot !== this.amount) {
          return
        }
        const payload = res.data || res
        const products = Array.isArray(payload.products) ? payload.products : []
        this.matchedProducts = products
        if (!products.length) {
          this.selectedProductId = ''
          this.selectedProduct = null
          return
        }

        const exact = products.find(item => item.exactMatch)
        this.selectProduct(exact || products[0], false)
      }).catch(() => {
        this.matchedProducts = []
        this.selectedProductId = ''
        this.selectedProduct = null
      }).finally(() => {
        this.matchLoading = false
      })
    },

    selectProduct(item, updateAmount = true) {
      if (!item) return
      this.selectedProductId = item.productId
      this.selectedProduct = item
      if (updateAmount && !item.customAmount) {
        this.amount = this.formatAmount(item.amount)
      }
    },

    pay() {
      this.tapFeedback(18)
      if (!this.selectedProductId) {
        return alert('请先选择商品')
      }

      const payAmount = this.selectedProduct && !this.selectedProduct.customAmount ? this.formatMoney(this.selectedProduct.amount) : this.amount
      getProductPayPackage(payAmount, this.selectedProductId, this.selectedWayCode || this.currentPayWay.wayCode).then(res => {
        if (res.code != '0') {
          return alert(res.msg)
        }

        this.resData = res.data
        if (Number(this.resData.orderState) === 2) {
          this.handlePaySuccess()
          return
        }
        if (Number(this.resData.orderState) !== 1) {
          return alert(res.data.errMsg || '订单状态异常')
        }
        if (this.selectedWayCode === config.payWay.WXPAY.wayCode) {
          this.invokeWxPay()
        } else {
          if (this.selectedWayCode === config.payWay.ALIPAY_QR.wayCode || this.selectedWayCode === config.payWay.ALIPAY_OC.wayCode) {
            this.openAlipayQrPay(this.resData)
          } else {
            this.invokeAliPay()
          }
        }
      }).catch(res => {
        this.$router.push({ name: config.errorPageRouteName, params: { errInfo: res.msg } })
      })
    },

    invokeWxPay() {
      if (!window.WeixinJSBridge) {
        if (document.addEventListener) {
          document.addEventListener('WeixinJSBridgeReady', this.onWxBridgeReady, false)
        } else if (document.attachEvent) {
          document.attachEvent('WeixinJSBridgeReady', this.onWxBridgeReady)
          document.attachEvent('onWeixinJSBridgeReady', this.onWxBridgeReady)
        }
      } else {
        this.onWxBridgeReady()
      }
    },

    onWxBridgeReady() {
      const bridge = window.WeixinJSBridge
      if (!bridge) {
        alert('当前环境未检测到微信支付组件，请在微信内打开')
        return
      }

      bridge.invoke('getBrandWCPayRequest', JSON.parse(this.resData.payInfo), res => {
        if (res.err_msg === 'get_brand_wcpay_request:ok') {
          finishSuccess(this.payOrderInfo.returnUrl)
          return
        }
        if (res.err_msg === 'get_brand_wcpay_request:cancel') {
          alert('支付取消')
          bridge.call('closeWindow')
          return
        }
        alert('支付失败:' + JSON.stringify(res))
        bridge.call('closeWindow')
      })
    },

    invokeAliPay() {
      if (!window.AlipayJSBridge) {
        document.addEventListener('AlipayJSBridgeReady', () => {
          this.doAliPay()
        }, false)
        return
      }
      this.doAliPay()
    },

    openAlipayQrPay(payData) {
      const targetUrl = payData.payData || payData.codeUrl || payData.payUrl
      if (targetUrl) {
        location.href = targetUrl
        return
      }

      if (payData.codeImgUrl) {
        location.href = payData.codeImgUrl
        return
      }

        alert('支付宝返回的支付链接为空，请检查商户收款能力')
    },

    handlePaySuccess() {
      finishSuccess(this.payOrderInfo.returnUrl)
    },

    doAliPay() {
      // eslint-disable-next-line no-undef
      AlipayJSBridge.call('tradePay', {
        tradeNO: this.resData.alipayTradeNo
      }, data => {
        if ('9000' == data.resultCode) {
          finishSuccess(this.payOrderInfo.returnUrl)
        } else if ('8000' == data.resultCode || '6004' == data.resultCode) {
          alert(JSON.stringify(data))
          window.AlipayJSBridge.call('closeWebview')
        } else {
          alert('用户已取消！')
          window.AlipayJSBridge.call('closeWebview')
        }
      })
    }
  }
}
</script>

<style lang="css" scoped>
.product-cashier-page {
  min-height: 100vh;
  background: #f5f6f8;
  color: #1f2733;
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", sans-serif;
  -webkit-text-size-adjust: 100%;
}

.product-main {
  padding: 42px 34px 430px;
}

.merchant-card {
  padding: 48px 44px 28px;
  border-radius: 24px;
  background: #fff;
  box-shadow: 0 18px 46px rgba(21, 52, 96, 0.07);
}

.merchant-row {
  display: flex;
  align-items: center;
}

.merchant-avatar {
  width: 64px;
  height: 64px;
  border-radius: 20px;
  background: #eef3ff;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex: 0 0 auto;
}

.merchant-avatar img {
  width: 38px;
  height: 38px;
}

.merchant-meta {
  margin-left: 18px;
  min-width: 0;
}

.pay-title {
  font-size: 22px;
  color: #6b7480;
  margin-bottom: 8px;
}

.merchant-name {
  font-size: 30px;
  line-height: 1.2;
  font-weight: 600;
  color: #1f2733;
  word-break: break-all;
}

.amount-box {
  margin-top: 30px;
  display: flex;
  align-items: flex-end;
  gap: 12px;
}

.currency {
  font-size: 32px;
  line-height: 1;
  color: #1f2733;
  padding-bottom: 10px;
}

.currency.ali {
  color: #1677ff;
}

.amount-text {
  font-size: 52px;
  line-height: 1;
  font-weight: 700;
  letter-spacing: 0;
  word-break: break-all;
}

.amount-text.empty {
  color: #b6bcc6;
  font-weight: 500;
  font-size: 34px;
  padding-bottom: 6px;
}

.match-hint {
  margin-top: 18px;
  font-size: 22px;
  color: #8b94a2;
}

.product-list {
  margin-top: 18px;
  display: grid;
  gap: 14px;
}

.product-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 18px;
  border-radius: 20px;
  border: 1px solid #e7ebf3;
  background: #fff;
  text-align: left;
}

.product-item.active {
  border-color: #1677ff;
  box-shadow: 0 8px 24px rgba(22, 119, 255, 0.12);
}

.product-item.exact {
  background: #f7fbff;
}

.product-cover {
  width: 68px;
  height: 68px;
  border-radius: 16px;
  background: #eef3ff;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: #1677ff;
  font-weight: 700;
}

.product-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-body {
  min-width: 0;
  flex: 1;
}

.product-name {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 28px;
  font-weight: 600;
  line-height: 1.2;
}

.product-name span {
  min-width: 0;
  word-break: break-all;
}

.product-tag {
  font-style: normal;
  font-size: 18px;
  line-height: 1;
  padding: 6px 10px;
  border-radius: 999px;
  background: #dbe8ff;
  color: #165dff;
  flex: 0 0 auto;
}

.product-tag.custom {
  background: #e8f7ef;
  color: #15945f;
}

.product-meta {
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px 18px;
  font-size: 20px;
  color: #7f8794;
}

.number-pad,
.fixed-pay-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 16px 18px 22px;
  background: rgba(245, 246, 248, 0.98);
  backdrop-filter: blur(18px);
  border-top: 1px solid #e6eaf2;
}

.key-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.num-key {
  height: 82px;
  border: 0;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 6px 16px rgba(21, 52, 96, 0.06);
  font-size: 28px;
  color: #1f2733;
}

.num-key.muted {
  color: #6b7480;
}

.pay-button {
  width: 100%;
  margin-top: 14px;
  height: 84px;
  border: 0;
  border-radius: 20px;
  background: linear-gradient(135deg, #1677ff, #0e5dd6);
  color: #fff;
  font-size: 30px;
  font-weight: 700;
}

.pay-button:disabled {
  background: #c6d1e6;
  color: rgba(255, 255, 255, 0.82);
}
</style>
